import { useContext, useCallback } from 'react'
import { App } from 'antd'
import { RecursosContext } from '../context/ConfiguracionRecursosContext'
import {
    RecursoRow,
    RecursoCreateRequest,
    ActualizarRecursoRequest,
    RecursoDetalle,
    ESTADOS_RECURSO,
} from '../interfaces/ConfiguracionRecursos.interfaces'
import {
    GetListadoRecursos,
    GetDetalleRecurso,
    CrearRecurso,
    EditarRecurso,
    CambiarEstadoRecurso,
} from '../services/ConfiguracionRecursos.service'

const useRecursos = () => {
    const context = useContext(RecursosContext)
    if (!context) throw new Error('useRecursos debe usarse dentro de RecursosProvider')

    const {
        rows, setRows,
        loadingTable, setLoadingTable,
        modalOpen, setModalOpen,
        modo, setModo,
        recursoSelected, setRecursoSelected,
        empresaFiltro, setEmpresaFiltro,
    } = context

    const { notification } = App.useApp()

    // Sin estación seleccionada se trae el listado completo que el backend
    // permita ver al usuario.
    const fetchListado = useCallback(async () => {
        setLoadingTable(true)
        try {
            const response = await GetListadoRecursos(empresaFiltro ?? undefined)
            setRows(response.data?.body ?? [])
        } catch (error) {
            notification.error({ message: 'Error', description: 'No se pudo cargar el listado de recursos.' })
        } finally {
            setLoadingTable(false)
        }
    }, [empresaFiltro])

    const openNew = () => {
        setRecursoSelected(null)
        setModo('crear')
        setModalOpen(true)
    }

    const openEdit = (record: RecursoRow) => {
        setRecursoSelected(record)
        setModo('editar')
        setModalOpen(true)
    }

    const openView = (record: RecursoRow) => {
        setRecursoSelected(record)
        setModo('ver')
        setModalOpen(true)
    }

    const closeModal = () => {
        setModalOpen(false)
        setRecursoSelected(null)
        setModo(null)
    }

    const crearRecurso = async (
        body: Omit<RecursoCreateRequest, 'empresa_ids'>,
        empresa_ids: number[]
    ) => {
        if (empresa_ids.length === 0) {
            notification.warning({ message: 'Advertencia', description: 'Selecciona al menos una estación.' })
            return { success: false }
        }
        if (body.archivos.length === 0) {
            notification.warning({ message: 'Advertencia', description: 'Adjunta al menos un archivo.' })
            return { success: false }
        }
        try {
            const response = await CrearRecurso({ ...body, empresa_ids })
            notification.success({
                message: 'Éxito',
                description:
                    response.data?.message ??
                    `${body.archivos.length} recurso(s) creado(s) en ${empresa_ids.length} estación(es).`,
            })
            fetchListado()
            return { success: true }
        } catch (error: any) {
            notification.error({
                message: 'Error',
                description: error?.response?.data?.message ?? 'No se pudo crear el recurso.',
            })
            return { success: false }
        }
    }

    /** Detalle con la ubicación resuelta, para precargar el modal de editar/ver. */
    const fetchDetalle = useCallback(async (recursoId: number): Promise<RecursoDetalle | null> => {
        try {
            const response = await GetDetalleRecurso(recursoId)
            return (response.data?.body ?? null) as RecursoDetalle | null
        } catch (error) {
            // El modal se abre igual con los datos que ya tenía la fila.
            return null
        }
    }, [])

    /**
     * Edita el recurso y, si el usuario adjuntó archivos extra, crea uno nuevo
     * por cada uno en la misma estación y del mismo tipo (el recurso apunta a un
     * solo archivo, así que "agregar más" significa crear más recursos ahí).
     */
    const editarRecurso = async (body: ActualizarRecursoRequest, archivosExtra: File[] = [], empresaId?: number) => {
        try {
            const response = await EditarRecurso(body)

            if (archivosExtra.length > 0) {
                if (!empresaId) {
                    notification.warning({
                        message: 'Advertencia',
                        description:
                            'El recurso se actualizó, pero no se pudo identificar su estación para crear los archivos adicionales.',
                    })
                    fetchListado()
                    return { success: true }
                }

                const responseExtra = await CrearRecurso({
                    empresa_ids: [empresaId],
                    tipo_id: body.tipo_id,
                    nombre: body.nombre,
                    estado_id: body.estado_id,
                    archivos: archivosExtra,
                })

                notification.success({
                    message: 'Éxito',
                    description: `Recurso actualizado. ${responseExtra.data?.message ?? `Se agregaron ${archivosExtra.length} recurso(s) más en la estación.`}`,
                })
                fetchListado()
                return { success: true }
            }

            notification.success({
                message: 'Éxito',
                description: response.data?.message ?? 'Recurso actualizado correctamente.',
            })
            fetchListado()
            return { success: true }
        } catch (error: any) {
            notification.error({
                message: 'Error',
                description: error?.response?.data?.message ?? 'No se pudo actualizar el recurso.',
            })
            return { success: false }
        }
    }

    /**
     * Inactivar borra el archivo del storage y deja el registro sin url, así que
     * para volver a activarlo hay que cargar uno nuevo desde la edición.
     */
    const toggleEstadoRecurso = async (record: RecursoRow) => {
        const activando = record.estado_id !== ESTADOS_RECURSO.ACTIVO

        if (activando && !record.url) {
            return {
                success: false,
                requiereArchivo: true,
                message: 'Este recurso no tiene archivo: edítalo y carga uno nuevo para poder activarlo.',
            }
        }

        try {
            const nuevoEstado = activando ? ESTADOS_RECURSO.ACTIVO : ESTADOS_RECURSO.INACTIVO
            const response = await CambiarEstadoRecurso({ recurso_id: record.id, estado_id: nuevoEstado })
            fetchListado()
            return { success: true, message: response.data?.message ?? 'Estado actualizado correctamente.' }
        } catch (error: any) {
            return {
                success: false,
                message: error?.response?.data?.message ?? 'No se pudo actualizar el estado del recurso.',
            }
        }
    }

    return {
        rows,
        loadingTable,
        modalOpen,
        modo,
        recursoSelected,
        empresaFiltro,
        setEmpresaFiltro,
        fetchListado,
        fetchDetalle,
        openNew,
        openEdit,
        openView,
        closeModal,
        crearRecurso,
        editarRecurso,
        toggleEstadoRecurso,
    }
}

export default useRecursos